/*
  bx-cocktail-strip-fill.js
  ----------------------------------------------------------------------
  Client-side enhancer for the cocktail strip on collection pages and
  producer-profile pages.

  WHY:
  Liquid's `shop.metaobjects.cocktail.values` caps at 50 server-side, and the
  `{% paginate by 250 %}` trick bumps it to 250 — but the catalog is 1,200+
  cocktails. Recently-imported producers (e.g. Drouin) sit past position 250
  and never get iterated by the server. Result: /collections/christian-drouin
  was showing "1 cocktail" instead of 60.

  WHAT:
  This script fetches the FULL cocktail catalog from the Storefront API
  (window.BX_STOREFRONT.token), filters client-side by a spec the section
  emits in <script type="application/json" data-bx-strip-config>, and replaces
  the server-rendered tiles with the full match set.

  HOW IT'S USED:
  Each section that wants this enhancement emits:
    1. The Storefront config (via `{% render 'bx-storefront-config' %}`)
    2. A filter spec — one of:
         { "mode": "producer",   "vendor": "Christian Drouin" }
         { "mode": "collection", "productHandles": ["handle-1", "handle-2", …] }
    3. A `<script src="…bx-cocktail-strip-fill.js" defer>` tag

  DESIGN NOTES:
   - Idempotent — multiple data-bx-strip-config blocks on one page each get
     their own augmentation.
   - Server-rendered tiles act as fallback when JS / Storefront is broken.
   - Recipe overlay markup matches the server-rendered tile structure for
     style consistency (uses the same .bx-cocktail-strip__* OR
     .bx-prod-profile__cocktail-* classes depending on which section).
*/

(function () {
  'use strict';

  /* Find every strip-augment block on the page. Each one carries its own
     filter spec + points at its target <ul>. */
  var blocks = document.querySelectorAll('[data-bx-strip-config]');
  if (!blocks.length) return;
  if (!window.BX_STOREFRONT || !window.BX_STOREFRONT.token) {
    console.warn('[bx-strip-fill] No BX_STOREFRONT config; skipping.');
    return;
  }

  /* Single cached fetch — share results across multiple blocks on one page. */
  var fetchPromise = null;
  function getAllCocktails() {
    if (fetchPromise) return fetchPromise;
    var cfg = window.BX_STOREFRONT;
    var endpoint = '/api/' + (cfg.apiVersion || '2024-10') + '/graphql.json';
    var query =
      'query Cocktails($cursor: String) {' +
      '  metaobjects(type: "cocktail", first: 250, after: $cursor) {' +
      '    nodes {' +
      '      handle' +
      '      fields {' +
      '        key value' +
      '        reference { ... on MediaImage { image { url altText } } }' +
      '        references(first: 25) { nodes { ... on Product { handle vendor } } }' +
      '      }' +
      '    }' +
      '    pageInfo { hasNextPage endCursor }' +
      '  }' +
      '}';

    var collected = [];
    function fetchPage(cursor) {
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token
        },
        body: JSON.stringify({ query: query, variables: { cursor: cursor } })
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j.errors) {
            console.error('[bx-strip-fill] Storefront errors', j.errors);
            return collected;
          }
          var page = j && j.data && j.data.metaobjects;
          if (!page) return collected;
          (page.nodes || []).forEach(function (n) { collected.push(n); });
          if (page.pageInfo && page.pageInfo.hasNextPage && collected.length < 5000) {
            return fetchPage(page.pageInfo.endCursor);
          }
          return collected;
        })
        .catch(function (err) {
          console.error('[bx-strip-fill] fetch failed', err);
          return collected;
        });
    }

    fetchPromise = fetchPage(null);
    return fetchPromise;
  }

  /* Normalize a Storefront node into a flat object we can render. */
  function normalize(node) {
    var c = { h: node.handle, u: [] };
    (node.fields || []).forEach(function (f) {
      if (!f) return;
      if (f.key === 'name')        c.n = f.value;
      else if (f.key === 'origin') c.o = f.value;
      else if (f.key === 'ingredients') c.i = f.value;
      else if (f.key === 'method')      c.m = f.value;
      else if (f.key === 'hero_photo' && f.reference && f.reference.image) {
        c.p = f.reference.image.url;
        c.alt = f.reference.image.altText || c.n || '';
      }
      else if (f.key === 'products_used' && f.references) {
        c.u = (f.references.nodes || []).map(function (p) {
          return { h: p.handle, v: p.vendor };
        });
      }
    });
    return c;
  }

  /* Does cocktail `c` match `spec`? */
  function matches(c, spec) {
    if (!c.u || !c.u.length) return false;
    if (spec.mode === 'producer') {
      if (!spec.vendor) return false;
      for (var i = 0; i < c.u.length; i++) {
        if (c.u[i].v === spec.vendor) return true;
      }
      return false;
    }
    if (spec.mode === 'collection') {
      var hSet = {};
      (spec.productHandles || []).forEach(function (h) { hSet[h] = 1; });
      for (var j = 0; j < c.u.length; j++) {
        if (hSet[c.u[j].h]) return true;
      }
      return false;
    }
    return false;
  }

  /* Build a tile <li> element using DOM API (safer than innerHTML for text).
     `classPrefix` toggles between the two section styles:
       'strip'       → .bx-cocktail-strip__*  (generic collection page)
       'producer'    → .bx-prod-profile__cocktail-* (producer page)
  */
  function renderTile(c, classPrefix) {
    if (!c.p) return null;
    var li = document.createElement('li');
    var photoCls, imgCls, overlayCls, overlayInnerCls, labelCls, textCls, nameCls;

    if (classPrefix === 'producer') {
      li.className = 'bx-prod-profile__cocktail-tile';
      photoCls       = 'bx-prod-profile__cocktail-photo';
      imgCls         = 'bx-prod-profile__cocktail-img';
      overlayCls     = 'bx-prod-profile__cocktail-overlay';
      overlayInnerCls= 'bx-prod-profile__cocktail-overlay-inner';
      labelCls       = 'bx-prod-profile__cocktail-section-label';
      textCls        = 'bx-prod-profile__cocktail-section-text';
      nameCls        = 'bx-prod-profile__cocktail-name';
    } else {
      li.className = 'bx-cocktail-strip__tile';
      photoCls       = 'bx-cocktail-strip__photo';
      imgCls         = 'bx-cocktail-strip__img';
      overlayCls     = 'bx-cocktail-strip__overlay';
      overlayInnerCls= 'bx-cocktail-strip__overlay-inner';
      labelCls       = 'bx-cocktail-strip__label';
      textCls        = 'bx-cocktail-strip__text';
      nameCls        = 'bx-cocktail-strip__name';
    }

    var photoDiv = document.createElement('div');
    photoDiv.className = photoCls;

    var img = document.createElement('img');
    /* Shopify image-url with width param — much smaller than the source. */
    img.src = c.p + (c.p.indexOf('?') === -1 ? '?' : '&') + 'width=480';
    img.alt = c.alt || c.n || 'Cocktail';
    img.loading = 'lazy';
    img.className = imgCls;
    photoDiv.appendChild(img);

    var overlay = document.createElement('div');
    overlay.className = overlayCls;
    overlay.setAttribute('aria-hidden', 'true');

    var inner = document.createElement('div');
    inner.className = overlayInnerCls;

    if (c.i) {
      var lblBuild = document.createElement('p');
      lblBuild.className = labelCls;
      lblBuild.textContent = 'Build';
      inner.appendChild(lblBuild);
      var txtBuild = document.createElement('p');
      txtBuild.className = textCls;
      /* Newlines → <br> for readability */
      String(c.i).split('\n').forEach(function (line, idx) {
        if (idx > 0) txtBuild.appendChild(document.createElement('br'));
        txtBuild.appendChild(document.createTextNode(line));
      });
      inner.appendChild(txtBuild);
    }
    if (c.m) {
      var lblMethod = document.createElement('p');
      lblMethod.className = labelCls;
      lblMethod.textContent = 'Method';
      inner.appendChild(lblMethod);
      var txtMethod = document.createElement('p');
      txtMethod.className = textCls;
      txtMethod.textContent = c.m;
      inner.appendChild(txtMethod);
    }

    overlay.appendChild(inner);
    photoDiv.appendChild(overlay);
    li.appendChild(photoDiv);

    var name = document.createElement('p');
    name.className = nameCls;
    name.textContent = c.n || '';
    li.appendChild(name);

    return li;
  }

  /* Update the strip's heading count. */
  function updateHeading(slider, count, vendorLabel) {
    var heading = slider.querySelector('.bx-cocktail-strip__heading, .bx-prod-profile__cocktails-heading');
    if (!heading) return;
    var label = vendorLabel ? (vendorLabel + ' cocktail') : 'cocktail';
    heading.innerHTML = count + ' ' + label + (count !== 1 ? 's' : '') + ' — hover for the recipe';
  }

  /* Process one strip-augment block. */
  function augmentBlock(configEl) {
    var spec;
    try {
      spec = JSON.parse(configEl.textContent || '{}');
    } catch (e) {
      console.error('[bx-strip-fill] Invalid spec JSON', e);
      return;
    }
    if (!spec.mode) return;

    /* Find the strip <ul> — sibling within the same section. */
    var section = configEl.closest('[data-bx-cocktails-slider], .bx-prod-profile__cocktails-row, .bx-cocktail-strip');
    if (!section) {
      section = configEl.parentElement;
    }
    var strip = section.querySelector('[data-bx-cocktails-strip], .bx-prod-profile__cocktails-strip');
    if (!strip) return;

    var classPrefix = strip.classList.contains('bx-prod-profile__cocktails-strip') ? 'producer' : 'strip';

    getAllCocktails().then(function (nodes) {
      var matched = [];
      for (var i = 0; i < nodes.length; i++) {
        var c = normalize(nodes[i]);
        if (matches(c, spec)) matched.push(c);
      }

      /* No matches — leave server fallback alone. */
      if (matched.length === 0) {
        /* But if the section was hidden because server matched 0, that's
           wrong — we just couldn't reach those cocktails server-side. Since
           we ALSO matched 0 client-side now, do nothing — there genuinely
           are no matching cocktails. */
        return;
      }

      /* Replace strip contents with the new tile set. */
      while (strip.firstChild) strip.removeChild(strip.firstChild);
      matched.forEach(function (c) {
        var tile = renderTile(c, classPrefix);
        if (tile) strip.appendChild(tile);
      });

      updateHeading(section, matched.length, spec.vendor || '');

      /* Re-show the section if its parent wrapper was display:none. */
      var hidden = section.closest('[hidden], [style*="display: none"]');
      if (hidden) {
        hidden.removeAttribute('hidden');
        hidden.style.display = '';
      }

      /* Re-fire the slider nav script that's already on the page. The
         server-rendered scripts bind onto [data-bx-bound] — they're
         already bound. We just need to refresh disabled-state on the
         arrows since strip.scrollWidth changed. */
      var event = new Event('scroll', { bubbles: true });
      strip.dispatchEvent(event);
    });
  }

  /* Kick off all blocks on the page. */
  for (var i = 0; i < blocks.length; i++) {
    augmentBlock(blocks[i]);
  }
})();
